import type { ICreateDocumentCmd } from "@/data/commands/document/CreateDocumentCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { DocumentDao } from "@/data/dao/service/DocumentDao";
import type { IDocument } from "@/data/model/Document";
import { Language } from "@/data/model/Enums";

import { i18n } from "@/portal/Trans";
import { FileUpload } from "@/portal/formControls/FileUpload";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";

export class DocumentForm extends BaseEntityForm<IDocument, ICreateDocumentCmd> {
    private publishedSwitch: Switch;
    private $nameField: JQuery;
    private displayNameField: MultiLanguageInput;
    private $orderField: JQuery;
    private filePicker: FileUpload;
    private model?: IDocument;

    constructor(element: JQuery) {
        super(element, Factory.getInstance(DocumentDao));

        this.publishedSwitch = new Switch(this.$form.find('input[name="ispublished"]'));

        this.$nameField = this.$form
            .find('.form-control[name="name"]')
            .change(() => this.updateName(this.$nameField.val()));
        this.displayNameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="displayname"]'));

        this.filePicker = new FileUpload({
            multiLanguage: true,
            allowedTypes: ["pdf", "application/octet-stream"],
            maxFileSizeInBytes: 15 * 1000000, //mb
        }).renderTo(this.$form.find("#fileupload > .col-lg-9"));

        this.$orderField = this.$form.find('.form-control[name="order"]')["TouchSpin"]({
            min: 1,
            max: 10000,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });
    }

    public setFormData(model?: IDocument) {
        this.model = model;
        this.updateName(model === undefined ? "" : model.name);
        this.publishedSwitch.setValue(model === undefined || false ? false : model.isPublished);
        this.$nameField.val(model === undefined ? "" : model.name);

        this.displayNameField.setValues(model?.displayName);

        this.filePicker.setValueForLanguage(model === undefined ? undefined : (model.fileDe as any), Language.German);
        this.filePicker.setValueForLanguage(model === undefined ? undefined : (model.fileEn as any), Language.English);
        this.filePicker.setValueForLanguage(model === undefined ? undefined : (model.fileIt as any), Language.Italian);
        this.filePicker.setValueForLanguage(model === undefined ? undefined : (model.fileFr as any), Language.French);

        this.$orderField.val(model === undefined ? undefined : (model.order as any));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateDocumentCmd {
        const data: ICreateDocumentCmd = {
            isPublished: this.publishedSwitch.getValue(),
            name: this.$nameField.val().length > 0 ? this.$nameField.val() : undefined,
            displayNameDe: this.displayNameField.getValue(Language.German),
            displayNameEn: this.displayNameField.getValue(Language.English),
            displayNameFr: this.displayNameField.getValue(Language.French),
            displayNameIt: this.displayNameField.getValue(Language.Italian),
            fileDe: this.filePicker.getValueForLanguage(Language.German),
            fileEn: this.filePicker.getValueForLanguage(Language.English),
            fileFr: this.filePicker.getValueForLanguage(Language.French),
            fileIt: this.filePicker.getValueForLanguage(Language.Italian),
            order: this.$orderField.val().length !== 0 ? parseInt(this.$orderField.val()) : undefined,
        };

        let hasError = false;

        //rule only for new documents
        this.filePicker.setError(false);

        if (this.id == null) {
            if (
                data.fileDe === undefined &&
                data.fileEn === undefined &&
                data.fileFr === undefined &&
                data.fileIt === undefined
            ) {
                hasError = true;
                this.filePicker.setError(true);
            }
        } else {
            if (data.fileDe === undefined) data.removeFileDe = this.filePicker.needsRemovalForLanguage(Language.German);

            if (data.fileEn === undefined)
                data.removeFileEn = this.filePicker.needsRemovalForLanguage(Language.English);

            if (data.fileFr === undefined) data.removeFileFr = this.filePicker.needsRemovalForLanguage(Language.French);

            if (data.fileIt === undefined)
                data.removeFileIt = this.filePicker.needsRemovalForLanguage(Language.Italian);

            //assert at least one file is set if others are getting removed
            if (
                (this.model!.fileDe === undefined || data.removeFileDe === true) &&
                data.fileDe === undefined &&
                (this.model!.fileEn === undefined || data.removeFileEn === true) &&
                data.fileEn === undefined &&
                (this.model!.fileFr === undefined || data.removeFileFr === true) &&
                data.fileFr === undefined &&
                (this.model!.fileIt === undefined || data.removeFileIt === true) &&
                data.fileFr === undefined
            ) {
                hasError = true;
                this.filePicker.setError(true);
            }
        }

        if (data.name == null || data.name.length === 0) {
            hasError = true;
            this.$nameField.addClass("is-invalid");
        }

        //check at least one display name is set
        // if (
        //     data.displayNameDe === undefined &&
        //     data.displayNameFr === undefined &&
        //     data.displayNameIt === undefined &&
        //     data.displayNameEn === undefined
        // ) {
        //     hasError = true;
        //     this.displayNameField.setError(true);
        // }

        if (data.order !== undefined && isNaN(data.order)) {
            hasError = true;
            this.$orderField.addClass("is-invalid");
        }

        return (hasError ? undefined : data) as any;
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case "document_limit_exceeded":
                return i18n("documents.failedLimitExceeded");
            case "no_active_subscription":
                return i18n("modal.tableBulk.failedNoSubscription");
        }

        return undefined;
    }
}
